import React from "react";
import { OAUTH_TOKEN_URLS } from "../destinationConstants";
import { Field, ConfigInput } from "./Field";

/**
 * Placeholder / helper-text lookup for the bearer-CRM types.
 *
 * We keep them in a plain object rather than a stack of nested ternaries so
 * new CRM types can be added by extending the map, not by editing branch
 * conditions.
 */
const ENDPOINT_PLACEHOLDER = {
  crm_salesforce: "https://<domain>.my.salesforce.com/services/data/v58.0/sobjects/Lead/",
  crm_litify: "https://<domain>.my.salesforce.com/services/data/v58.0/sobjects/Litify_pm__Intake__c/",
  crm_lawmatics: "https://api.lawmatics.com/v1/forms/<form_uuid>/submit",
  crm_hubspot: "https://api.hubapi.com/crm/v3/objects/contacts (default)",
};

const AUTH_TOKEN_PLACEHOLDER = {
  crm_lawmatics: "OAuth access token",
  crm_hubspot: "pat-… (from Settings → Integrations → Private Apps)",
  crm_salesforce: "Session ID from a Connected App",
  crm_litify: "Session ID from a Connected App",
};

const AUTH_TOKEN_HINT = {
  crm_lawmatics: "Obtain from POST https://api.lawmatics.com/oauth/token. We POST lead fields as JSON.",
  crm_hubspot: "Create a Private App with the crm.objects.contacts.write scope, then copy the access token.",
  crm_salesforce: "We send fields mapped to Lead field names. Failed requests retry with backoff.",
  crm_litify: "We send fields mapped to Litify_pm__Intake__c field names. Failed requests retry with backoff.",
};

function StaticBearerBlock({ type, config, setConfig }) {
  return (
    <Field
      label={type === "crm_hubspot" ? "Private App access token" : "Bearer token"}
      required
      colSpan={2}
      hint={AUTH_TOKEN_HINT[type]}
    >
      <ConfigInput
        name="auth_token" config={config} setConfig={setConfig}
        testId="dest-auth-token" required type="password"
        placeholder={AUTH_TOKEN_PLACEHOLDER[type]}
      />
    </Field>
  );
}

function OAuthBlock({ type, config, setConfig }) {
  const defaultUrl = OAUTH_TOKEN_URLS[type];
  return (
    <>
      <Field label="OAuth client ID" required>
        <ConfigInput name="oauth_client_id" config={config} setConfig={setConfig}
          testId="dest-oauth-client-id" required
          placeholder={type === "crm_hubspot" ? "HubSpot app client id" : "Connected App consumer key"} />
      </Field>
      <Field label="OAuth client secret" required>
        <ConfigInput name="oauth_client_secret" config={config} setConfig={setConfig}
          testId="dest-oauth-client-secret" required type="password"
          placeholder="Consumer secret" />
      </Field>
      <Field label="Refresh token" required colSpan={2}>
        <ConfigInput name="oauth_refresh_token" config={config} setConfig={setConfig}
          testId="dest-oauth-refresh" required type="password"
          placeholder="Long-lived refresh token from the initial OAuth grant" />
      </Field>
      <Field label="Token URL" colSpan={2}
        hint={<>Defaults to <span className="font-mono">{defaultUrl}</span>. Override for sandboxes.</>}>
        <ConfigInput name="oauth_token_url" config={config} setConfig={setConfig}
          testId="dest-oauth-token-url" placeholder={defaultUrl} className="text-xs" />
      </Field>
    </>
  );
}

function OAuthToggle({ type, config, setConfig }) {
  let modeHint;
  if (config.oauth_mode) modeHint = "Access tokens auto-rotate";
  else if (type === "crm_hubspot") modeHint = "Private App Bearer token (static)";
  else modeHint = "Static Bearer token (expires manually)";

  return (
    <div className="col-span-2 flex items-center gap-2 border border-dashed border-zinc-400 p-2">
      <input type="checkbox" checked={!!config.oauth_mode}
        onChange={(e) => setConfig("oauth_mode", e.target.checked)}
        data-testid="dest-oauth-toggle"
        className="border-2 border-black h-4 w-4" />
      <label className="text-[11px] uppercase tracking-widest font-bold">
        Use OAuth refresh-token flow
      </label>
      <span className="text-[10px] text-zinc-500 ml-auto">{modeHint}</span>
    </div>
  );
}

/**
 * Field set for `crm_salesforce`, `crm_litify`, `crm_lawmatics`, `crm_hubspot`.
 *
 * The four CRMs share the same auth shape (Bearer token OR OAuth refresh flow),
 * so we render one component with a per-CRM placeholder map. Adding a fifth
 * bearer CRM = adding a row to the placeholder maps at the top of the file.
 */
export default function BearerCrmFields({ type, config, setConfig }) {
  const isHubspot = type === "crm_hubspot";
  return (
    <>
      <Field label="REST endpoint URL" required colSpan={2}
        hint={isHubspot ? (
          <>
            Leave blank to use the default HubSpot contacts endpoint. Fields are wrapped in{" "}
            <span className="font-mono">{"{ properties: {...} }"}</span>.
          </>
        ) : null}>
        <ConfigInput name="endpoint" config={config} setConfig={setConfig}
          testId="dest-endpoint" required placeholder={ENDPOINT_PLACEHOLDER[type]} className="text-xs" />
      </Field>

      <OAuthToggle type={type} config={config} setConfig={setConfig} />

      {config.oauth_mode
        ? <OAuthBlock type={type} config={config} setConfig={setConfig} />
        : <StaticBearerBlock type={type} config={config} setConfig={setConfig} />}
    </>
  );
}
